import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Upload, File, Image, FileText, Trash2, Download,
    Eye, MoreVertical, Search, Filter, Loader2, X, Check,
    AlertCircle, FolderOpen, Calendar, User, Home
} from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

interface Document {
    id: string;
    name: string;
    type: string;
    category: 'lease' | 'id' | 'property' | 'maintenance' | 'financial' | 'other';
    size: number;
    uploadedAt: string;
    uploadedBy: string;
    tenantName?: string;
    unitNumber?: string;
    propertyName?: string;
    url: string;
}

type DocumentCategory = 'all' | 'lease' | 'id' | 'property' | 'maintenance' | 'financial' | 'other';

const DocumentManager: React.FC = () => {
    const navigate = useNavigate();
    const { success, error: toastError } = useToast();
    const { properties, tenants } = useLandlordStore();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<DocumentCategory>('all');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
    const [showDocActions, setShowDocActions] = useState<string | null>(null);

    // Upload form state
    const [uploadData, setUploadData] = useState({
        name: '',
        category: 'other' as Document['category'],
        tenantId: '',
        propertyId: '',
        file: null as File | null
    });

    useEffect(() => {
        fetchDocuments();
    }, []);

    useEffect(() => {
        filterDocuments();
    }, [documents, searchQuery, selectedCategory]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/documents');
            setDocuments(response.data);
        } catch (err) {
            console.error('Failed to fetch documents:', err);
            // Use mock data for demo
            setDocuments(getMockDocuments());
        } finally {
            setLoading(false);
        }
    };

    const getMockDocuments = (): Document[] => [
        {
            id: '1',
            name: 'Lease Agreement - Unit A1',
            type: 'application/pdf',
            category: 'lease',
            size: 245000,
            uploadedAt: '2024-01-15T10:30:00Z',
            uploadedBy: 'John Landlord',
            tenantName: 'Jane Tenant',
            unitNumber: 'A1',
            propertyName: 'Sunrise Apartments',
            url: '#'
        },
        {
            id: '2',
            name: 'Tenant ID - Jane Tenant',
            type: 'image/jpeg',
            category: 'id',
            size: 150000,
            uploadedAt: '2024-01-10T14:20:00Z',
            uploadedBy: 'John Landlord',
            tenantName: 'Jane Tenant',
            unitNumber: 'A1',
            propertyName: 'Sunrise Apartments',
            url: '#'
        },
        {
            id: '3',
            name: 'Property Inspection Report',
            type: 'application/pdf',
            category: 'property',
            size: 890000,
            uploadedAt: '2024-01-08T09:15:00Z',
            uploadedBy: 'John Landlord',
            propertyName: 'Sunrise Apartments',
            url: '#'
        },
        {
            id: '4',
            name: 'Plumbing Repair Invoice',
            type: 'application/pdf',
            category: 'maintenance',
            size: 120000,
            uploadedAt: '2024-01-05T16:45:00Z',
            uploadedBy: 'John Landlord',
            unitNumber: 'B2',
            propertyName: 'Sunrise Apartments',
            url: '#'
        },
        {
            id: '5',
            name: 'Rent Receipt - December',
            type: 'application/pdf',
            category: 'financial',
            size: 85000,
            uploadedAt: '2024-01-01T11:00:00Z',
            uploadedBy: 'John Landlord',
            tenantName: 'Bob Smith',
            unitNumber: 'B2',
            propertyName: 'Sunrise Apartments',
            url: '#'
        }
    ];

    const filterDocuments = () => {
        let filtered = [...documents];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(doc =>
                doc.name.toLowerCase().includes(query) ||
                doc.tenantName?.toLowerCase().includes(query) ||
                doc.unitNumber?.toLowerCase().includes(query) ||
                doc.propertyName?.toLowerCase().includes(query)
            );
        }

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(doc => doc.category === selectedCategory);
        }

        setFilteredDocuments(filtered);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadData(prev => ({ ...prev, file, name: file.name }));
        }
    };

    const handleUpload = async () => {
        if (!uploadData.file || !uploadData.name) {
            toastError('Please select a file');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadData.file);
            formData.append('name', uploadData.name);
            formData.append('category', uploadData.category);
            if (uploadData.tenantId) formData.append('tenantId', uploadData.tenantId);
            if (uploadData.propertyId) formData.append('propertyId', uploadData.propertyId);

            await apiClient.post('/documents/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            success('Document uploaded successfully!');
            setShowUploadModal(false);
            fetchDocuments();
            resetUploadForm();
        } catch (err) {
            console.error('Failed to upload document:', err);
            // Add to local state for demo
            const newDoc: Document = {
                id: Date.now().toString(),
                name: uploadData.name,
                type: uploadData.file?.type || 'application/octet-stream',
                category: uploadData.category,
                size: uploadData.file?.size || 0,
                uploadedAt: new Date().toISOString(),
                uploadedBy: 'Current User',
                tenantName: uploadData.tenantId ? tenants.find(t => t.id === uploadData.tenantId)?.full_name : undefined,
                unitNumber: uploadData.tenantId ? tenants.find(t => t.id === uploadData.tenantId)?.unit_number : undefined,
                propertyName: uploadData.propertyId ? properties.find(p => p.id === uploadData.propertyId)?.name : undefined,
                url: '#'
            };
            setDocuments(prev => [newDoc, ...prev]);
            success('Document uploaded successfully!');
            setShowUploadModal(false);
            resetUploadForm();
        } finally {
            setUploading(false);
        }
    };

    const resetUploadForm = () => {
        setUploadData({
            name: '',
            category: 'other',
            tenantId: '',
            propertyId: '',
            file: null
        });
    };

    const handleDelete = async (docId: string) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        try {
            await apiClient.delete(`/documents/${docId}`);
            success('Document deleted successfully!');
            setDocuments(prev => prev.filter(d => d.id !== docId));
        } catch (err) {
            console.error('Failed to delete document:', err);
            // Remove from local state for demo
            setDocuments(prev => prev.filter(d => d.id !== docId));
            success('Document deleted successfully!');
        }
        setShowDocActions(null);
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getCategoryColor = (category: string) => {
        const colors: { [key: string]: string } = {
            lease: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
            id: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
            property: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            maintenance: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
            financial: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
            other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
        };
        return colors[category] || colors.other;
    };

    const getCategoryIcon = (type: string) => {
        if (type.startsWith('image/')) return Image;
        if (type === 'application/pdf') return FileText;
        return File;
    };

    const categories: { value: DocumentCategory; label: string }[] = [
        { value: 'all', label: 'All Documents' },
        { value: 'lease', label: 'Lease Agreements' },
        { value: 'id', label: 'ID Documents' },
        { value: 'property', label: 'Property Documents' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'financial', label: 'Financial' },
        { value: 'other', label: 'Other' }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/landlord')}
                        className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                    >
                        <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Document Manager</h1>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                            Upload and manage property and tenant documents
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Upload size={18} />
                    Upload Document
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Total Documents</div>
                    <div className="text-2xl font-bold text-light-text dark:text-dark-text">{documents.length}</div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Lease Agreements</div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {documents.filter(d => d.category === 'lease').length}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Property Docs</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {documents.filter(d => d.category === 'property').length}
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm text-light-subtle dark:text-dark-subtle mb-1">Financial Docs</div>
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                        {documents.filter(d => d.category === 'financial').length}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search documents..."
                            className="w-full pl-10 pr-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {categories.map(cat => (
                            <button
                                key={cat.value}
                                onClick={() => setSelectedCategory(cat.value)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    selectedCategory === cat.value
                                        ? 'bg-primary text-white'
                                        : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text hover:bg-light-bg/80 dark:hover:bg-dark-bg/80'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Document List */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border">
                <div className="p-4 border-b border-light-border dark:border-dark-border">
                    <h2 className="font-bold text-light-text dark:text-dark-text">
                        Documents ({filteredDocuments.length})
                    </h2>
                </div>

                {filteredDocuments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <FolderOpen size={48} className="text-light-subtle dark:text-dark-subtle mb-4" />
                        <p className="text-light-subtle dark:text-dark-subtle">No documents found</p>
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="mt-4 text-primary hover:underline"
                        >
                            Upload your first document
                        </button>
                    </div>
                ) : (
                    <div className="divide-y divide-light-border dark:divide-dark-border">
                        {filteredDocuments.map(doc => {
                            const DocIcon = getCategoryIcon(doc.type);
                            return (
                                <div
                                    key={doc.id}
                                    className="flex items-center justify-between p-4 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                                >
                                    <div className="flex items-center gap-4 flex-1">
                                        <div className="w-12 h-12 rounded-lg bg-light-bg dark:bg-dark-bg flex items-center justify-center">
                                            <DocIcon size={24} className="text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-medium text-light-text dark:text-dark-text truncate">
                                                    {doc.name}
                                                </h3>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(doc.category)}`}>
                                                    {doc.category}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-light-subtle dark:text-dark-subtle mt-1">
                                                <span>{formatFileSize(doc.size)}</span>
                                                <span>{formatDate(doc.uploadedAt)}</span>
                                                {doc.tenantName && (
                                                    <span className="flex items-center gap-1">
                                                        <User size={12} />
                                                        {doc.tenantName}
                                                    </span>
                                                )}
                                                {doc.unitNumber && (
                                                    <span className="flex items-center gap-1">
                                                        <Home size={12} />
                                                        Unit {doc.unitNumber}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setSelectedDoc(doc)}
                                            className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                            title="Preview"
                                        >
                                            <Eye size={18} className="text-light-text dark:text-dark-text" />
                                        </button>
                                        <button
                                            onClick={() => {}}
                                            className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                            title="Download"
                                        >
                                            <Download size={18} className="text-light-text dark:text-dark-text" />
                                        </button>
                                        <div className="relative">
                                            <button
                                                onClick={() => setShowDocActions(showDocActions === doc.id ? null : doc.id)}
                                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                            >
                                                <MoreVertical size={18} className="text-light-text dark:text-dark-text" />
                                            </button>
                                            {showDocActions === doc.id && (
                                                <div className="absolute right-0 top-full mt-1 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg shadow-lg z-50 min-w-[150px]">
                                                    <button
                                                        onClick={() => handleDelete(doc.id)}
                                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 w-full max-w-lg border border-light-border dark:border-dark-border">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Upload Document</h2>
                            <button
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                            >
                                <X size={20} className="text-light-text dark:text-dark-text" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* File Upload Area */}
                            <div
                                onClick={() => document.getElementById('fileInput')?.click()}
                                className="border-2 border-dashed border-light-border dark:border-dark-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                            >
                                <Upload size={48} className="mx-auto text-light-subtle dark:text-dark-subtle mb-4" />
                                <p className="text-light-text dark:text-dark-text font-medium mb-2">
                                    {uploadData.file ? uploadData.file.name : 'Click to select a file'}
                                </p>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                    or drag and drop here
                                </p>
                                <input
                                    id="fileInput"
                                    type="file"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                />
                            </div>

                            {/* Document Name */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Document Name
                                </label>
                                <input
                                    type="text"
                                    value={uploadData.name}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Enter document name"
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Category
                                </label>
                                <select
                                    value={uploadData.category}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value as Document['category'] }))}
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    {categories.filter(c => c.value !== 'all').map(cat => (
                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Link to Tenant */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Link to Tenant (Optional)
                                </label>
                                <select
                                    value={uploadData.tenantId}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, tenantId: e.target.value }))}
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="">Select a tenant...</option>
                                    {tenants.filter(t => t.status === 'Active').map(tenant => (
                                        <option key={tenant.id} value={tenant.id}>
                                            {tenant.full_name} - Unit {tenant.unit_number}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Link to Property */}
                            <div>
                                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                                    Link to Property (Optional)
                                </label>
                                <select
                                    value={uploadData.propertyId}
                                    onChange={(e) => setUploadData(prev => ({ ...prev, propertyId: e.target.value }))}
                                    className="w-full px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="">Select a property...</option>
                                    {properties.map(property => (
                                        <option key={property.id} value={property.id}>
                                            {property.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4 mt-6">
                            <button
                                type="button"
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                className="flex-1 px-4 py-2 border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleUpload}
                                disabled={uploading || !uploadData.file}
                                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                {uploading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 size={18} className="animate-spin" />
                                        Uploading...
                                    </span>
                                ) : (
                                    'Upload'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {selectedDoc && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 w-full max-w-2xl border border-light-border dark:border-dark-border">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Document Preview</h2>
                            <button
                                onClick={() => setSelectedDoc(null)}
                                className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                            >
                                <X size={20} className="text-light-text dark:text-dark-text" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-lg bg-light-bg dark:bg-dark-bg flex items-center justify-center">
                                    {(() => {
                                        const DocIcon = getCategoryIcon(selectedDoc.type);
                                        return <DocIcon size={32} className="text-primary" />;
                                    })()}
                                </div>
                                <div>
                                    <h3 className="font-bold text-light-text dark:text-dark-text">{selectedDoc.name}</h3>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                        {formatFileSize(selectedDoc.size)} • {formatDate(selectedDoc.uploadedAt)}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-light-subtle dark:text-dark-subtle">Category:</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(selectedDoc.category)}`}>
                                        {selectedDoc.category}
                                    </span>
                                </div>
                                {selectedDoc.tenantName && (
                                    <div className="flex justify-between">
                                        <span className="text-light-subtle dark:text-dark-subtle">Tenant:</span>
                                        <span className="text-light-text dark:text-dark-text">{selectedDoc.tenantName}</span>
                                    </div>
                                )}
                                {selectedDoc.unitNumber && (
                                    <div className="flex justify-between">
                                        <span className="text-light-subtle dark:text-dark-subtle">Unit:</span>
                                        <span className="text-light-text dark:text-dark-text">{selectedDoc.unitNumber}</span>
                                    </div>
                                )}
                                {selectedDoc.propertyName && (
                                    <div className="flex justify-between">
                                        <span className="text-light-subtle dark:text-dark-subtle">Property:</span>
                                        <span className="text-light-text dark:text-dark-text">{selectedDoc.propertyName}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-light-subtle dark:text-dark-subtle">Uploaded by:</span>
                                    <span className="text-light-text dark:text-dark-text">{selectedDoc.uploadedBy}</span>
                                </div>
                            </div>

                            {/* Preview placeholder */}
                            <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-8 text-center">
                                <File size={48} className="mx-auto text-light-subtle dark:text-dark-subtle mb-4" />
                                <p className="text-light-subtle dark:text-dark-subtle">
                                    Preview not available for this file type
                                </p>
                                <button className="mt-4 text-primary hover:underline">
                                    Download to view
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4 mt-6">
                            <button
                                onClick={() => {}}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors font-medium"
                            >
                                <Download size={18} />
                                Download
                            </button>
                            <button
                                onClick={() => { handleDelete(selectedDoc.id); setSelectedDoc(null); }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                            >
                                <Trash2 size={18} />
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentManager;